import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Add01Icon, ArrowDown01Icon } from "@hugeicons/core-free-icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface FilterMenuOption {
  value: string;
  label: string;
  keywords?: string[];
}

interface FilterMenuProps {
  label: string;
  options: FilterMenuOption[];
  /**
   * Single-select: pass a string (e.g. "all" or an option value).
   * Multi-select:  pass a string[] (empty array = nothing selected).
   */
  value: string | string[];
  onValueChange: (value: string) => void;
  defaultValue?: string;
  /** When true the menu stays open on each pick and tracks multiple selections. */
  multiple?: boolean;
  disabled?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  emptyText?: string;
  /**
   * "tags" (default): shadcn's faceted-filter trigger — "+ Label | [a] [b] ⌄",
   * collapsing to "N selected" past two. "text": the older "Label: a, b".
   */
  selectedDisplay?: "text" | "tags";
}

export const FilterMenu = ({
  label,
  options,
  value,
  onValueChange,
  defaultValue = "all",
  multiple = false,
  disabled = false,
  triggerClassName,
  contentClassName,
  emptyText = "No results found",
  selectedDisplay = "tags",
}: FilterMenuProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const visibleOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter((option) => {
      const haystack = [option.label, ...(option.keywords || [])]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [options, query]);

  const selectedLabels = useMemo(() => {
    if (multiple) {
      const selected = value as string[];
      return options
        .filter((o) => selected.includes(o.value))
        .map((o) => o.label);
    }
    const single = value as string;
    if (single && single !== defaultValue) {
      const found = options.find((o) => o.value === single);
      return found ? [found.label] : [];
    }
    return [];
  }, [value, options, multiple, defaultValue]);

  const isActive = selectedLabels.length > 0;

  const pick = (optionValue: string) => {
    onValueChange(optionValue);
    if (!multiple) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn(
            "max-w-xs",
            isActive && selectedDisplay === "text" && "bg-muted",
            selectedDisplay === "tags" && "max-w-none border-dashed",
            triggerClassName
          )}
        >
          {selectedDisplay === "tags" ? (
            <>
              <HugeiconsIcon icon={Add01Icon} size={15} strokeWidth={2} />
              <span>{label}</span>
              {isActive && (
                <>
                  <Separator orientation="vertical" className="mx-1 h-4" />
                  {selectedLabels.length > 2 ? (
                    <Badge variant="neutral" className="rounded-sm px-1.5">
                      {selectedLabels.length} selected
                    </Badge>
                  ) : (
                    selectedLabels.map((selected) => (
                      <Badge
                        key={selected}
                        variant="neutral"
                        className="rounded-sm px-1.5"
                      >
                        {selected}
                      </Badge>
                    ))
                  )}
                </>
              )}
              <HugeiconsIcon
                icon={ArrowDown01Icon}
                size={15}
                strokeWidth={2}
                className={cn(
                  "ml-0.5 text-muted-foreground transition-transform",
                  open && "rotate-180"
                )}
              />
            </>
          ) : isActive ? (
            <span className="truncate">
              <span className="text-muted-foreground">{label}:</span>{" "}
              {selectedLabels.join(", ")}
            </span>
          ) : (
            <>
              <HugeiconsIcon icon={Add01Icon} size={15} strokeWidth={2} />
              <span>{label}</span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("w-72 overflow-hidden p-0", contentClassName)}
      >
        <div className="border-b border-border px-3 py-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={label}
              className="h-auto border-0 bg-transparent px-0 py-0 pl-7 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto p-1.5">
          {visibleOptions.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {emptyText}
            </div>
          ) : (
            visibleOptions.map((option) => {
              const isSelected = multiple
                ? (value as string[]).includes(option.value)
                : value === option.value;

              return (
                // A div, not a <button>: the Checkbox inside is itself a
                // button, and a button can't contain one.
                <div
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  onClick={() => pick(option.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      pick(option.value);
                    }
                  }}
                  className={cn(
                    "cursor-pointer",
                    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    isSelected && "bg-muted"
                  )}
                >
                  <Checkbox
                    checked={isSelected}
                    tabIndex={-1}
                    aria-hidden
                    className="pointer-events-none h-5 w-5 rounded-md border-input data-[state=checked]:border-primary data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary"
                  />
                  <span className="leading-none">{option.label}</span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
